import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import classNames from 'classnames';

import { ComponentPropsWithRef } from '../../types/utils';

export type CheckboxRadioInputOwnProps = {
  type?: 'checkbox' | 'radio';
  hasError?: boolean;
  indeterminate?: boolean;
  rounded?: boolean;
};

export type CheckboxRadioInputProps = ComponentPropsWithRef<'input'> &
  CheckboxRadioInputOwnProps;

export const CheckboxRadioInput = forwardRef<
  HTMLInputElement,
  CheckboxRadioInputProps
>(function CheckboxRadioInput(
  {
    type = 'checkbox',
    className,
    disabled,
    hasError = false,
    indeterminate = false,
    rounded = false,
    children,
    ...rest
  },
  ref
) {
  const innerRef = useRef<HTMLInputElement>(null!);
  useImperativeHandle(ref, () => innerRef.current);
  useEffect(() => {
    innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={innerRef}
      type={type}
      disabled={disabled}
      aria-invalid={hasError || undefined}
      className={classNames(
        'appearance-none w-4 h-4 border-2',
        'bg-center bg-no-repeat bg-cover',
        'focus:outline-none focus:ring focus:ring-opacity-50',
        hasError
          ? 'border-red-500 focus:ring-red-500'
          : 'border-teal-500 focus:ring-teal-500',
        type === 'checkbox' && [
          rounded ? 'rounded-full' : 'rounded',
          hasError
            ? 'checked:bg-checkbox-invalid indeterminate:bg-checkbox-indeterminate-invalid'
            : 'checked:bg-checkbox indeterminate:bg-checkbox-indeterminate',
        ],
        type === 'radio' && [
          'rounded-full',
          hasError ? 'checked:bg-radio-invalid' : 'checked:bg-radio',
        ],
        { 'opacity-50': disabled },
        className
      )}
      {...rest}
    >
      {children}
    </input>
  );
});
